import React from 'react';

/**
 * Hook that handles switching between design and preview modes based on messages from the designer app.
 *
 * The `window.__wf_design` is a global flag that indicates whether the site is in "design mode" or "preview mode".
 * This flag controls how Webflow's interactive elements (like dropdowns, sliders)
 * behave in different modes:
 *
 * - When true (design mode):
 *   - Interactive elements are initialized for editing
 *   - Dropdowns can be opened/closed via settings for editing their contents
 *   - Sliders disable auto-play and navigation events for editing
 *
 * - When false (preview mode):
 *   - Interactive elements behave like they would on the live site
 *   - Dropdowns collapse/expand on hover/click
 *   - Sliders animate between slides
 *
 * When mode changes, we use `window.Webflow.require()` to get each interactive module
 * (dropdown, slider, etc) and call either their `design()` or `preview()` method
 * to update their behavior accordingly.
 *
 * @param designerAppOrigin - Origin of the designer app, used to validate incoming messages
 */
export const useDesignModeHandler = (designerAppOrigin: string | null) => {
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!designerAppOrigin || event.origin !== designerAppOrigin) {
        return;
      }

      const {method, value} = event.data;
      if (method === 'setDesignerEditMode') {
        window.__wf_design = value;

        if (window.Webflow?.require) {
          const modules: Parameters<typeof window.Webflow.require>[0][] = [
            'dropdown',
            'slider',
          ];
          modules.forEach((moduleName) => {
            const module = window.Webflow.require(moduleName);
            if (module) {
              if (value) {
                module.design?.();
              } else {
                module.preview?.();
              }
            }
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [designerAppOrigin]);
};
